import React, { useEffect, useState, useRef } from 'react';
import { isEmpty, xor, get } from 'lodash';
import axios from 'axios';
import {
	SdfBadge,
	SdfBusyIndicator,
	SdfGrid,
	SdfLink,
	SdfSelect,
	SdfxPagination
} from '@synerg/react-components';
import { AgGridReact } from 'ag-grid-react';
import { getFormattedDate } from '../common';
import SubscriberDetails from './subscriberDetails';
import './subscriptionsDetails.scss';

const CONSENT_TYPES = {
	requestsForAuth: 'REQUESTED',
	deniedAuth: 'DENIED',
	allowedAuth: 'ALLOWED'
};

const PAGE_SIZES = [
	{ label: '10', value: 10 },
	{ label: '20', value: 20 },
	{ label: '40', value: 40 },
	{ label: '60', value: 60 },
	{ label: '80', value: 80 },
	{ label: '100', value: 100 }
];
    
export default function SubscriptionsTable({ subscriptionList, retrieveOrgName, projectId, type,
	cutOffDate, isLoadingSubscriptions, selectedProjectType, activeCR = false, filterClientOrOrg,
	filterSubStatus }) {

	const [modalOpen, setModalOpen] = useState(false);
	const [associateDetails, setAssociateDetails] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [shouldDisplayAlertError, setShouldDisplayAlertError] = useState(false);
	const [selectedRow, setSelectedRow] = useState({});
	const [ pageNo, setPageNo ] = useState(1);
	const [ pageSize, setPageSize ] = useState(10);
	const [ sortOrder, setSortOrder ] = useState({ colId: 'organizationName', sortBy: 'asc' });
	const [ pageTableData, setPageTableData ] = useState([]);
	const [ availablePageSizes, setAvailablePageSizes] = useState([]);

	useEffect(() => {
		let pageData = subscriptionList;

		if (sortOrder.colId === 'consentDateTime') {
			// eslint-disable-next-line array-callback-return
			pageData = pageData.sort((a, b) => {
				if (sortOrder.sortBy === 'desc') {
					return new Date(b.consentDateTime) - new Date(a.consentDateTime);
				} else if (sortOrder.sortBy === 'asc') {
					return new Date(a.consentDateTime) - new Date(b.consentDateTime);
				}
			});
		}
		if (sortOrder.colId === 'organizationName') {
			// eslint-disable-next-line array-callback-return
			pageData = pageData.sort((a, b) => {
				if (sortOrder.sortBy === 'desc') {
					return b[sortOrder.colId].toLowerCase().localeCompare(a[sortOrder.colId].toLowerCase());
				} else if (sortOrder.sortBy === 'asc') {
					return a[sortOrder.colId].toLowerCase().localeCompare(b[sortOrder.colId].toLowerCase());
				}
			});
		}
		setPageTableData(pageData);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [subscriptionList, sortOrder]);

	useEffect(() => {
		setAvailablePageSizes([]);
		if (pageTableData.length) {
			PAGE_SIZES.every((element) => {
				if (pageTableData.length > element.value) {
					setAvailablePageSizes((prevState) => ([ ...prevState, element ]));
					return true;
				}
				else {
					setAvailablePageSizes((prevState) => ([ ...prevState, element ]));
					return false;
				}
			})
		}
		setPageSize(10);
	}, [pageTableData]);

	const total = pageTableData.length;
	const start = total > 0 ? (pageNo - 1) * pageSize + 1 : 0;
	const end = (pageNo * pageSize) > total ? total : (pageNo * pageSize);
	let totalPages = 1;
	if (total % pageSize === 0 && total !== 0) {
		totalPages = (total - total % pageSize) / pageSize;
	} else {
		totalPages = ((total - total % pageSize) / pageSize) + 1;
	}
	let paginatedData = pageTableData.slice((pageNo - 1) * pageSize, pageNo * pageSize);

	useEffect(() => {
		setPageNo(1);
	}, [pageSize, filterClientOrOrg, filterSubStatus]);

	const onSortChanged = (params) => {
		let columnWithSort = params.columnApi.getColumnState().find(col => col.sort !== null);
		if (columnWithSort) {
			setSortOrder({ colId: columnWithSort.colId, sortBy: columnWithSort.sort });
		} else {
			setSortOrder({ colId: '', sortBy: '' });
		}
	};

	const myIcons = {
		first: "<sdf-icon icon='nav-page-first' />",
		previous: "<sdf-icon icon='nav-page-back' />",
		next: "<sdf-icon icon='nav-page-next' />",
		last: "<sdf-icon icon='nav-page-last' />",
		sortAscending: "<sdf-icon icon='action-sort-asc' />",
		sortDescending: "<sdf-icon icon='action-sort-desc' />",
		sortUnSort: "<sdf-icon icon='action-sort' />"
	};

	const defaultColDef = {
		sortable: true,
		filter: false,
		resizable: false,
		wrapText: true,
		suppressMovable: true,
		suppressMenu: true,
		autoHeight: true,
		wrapHeaderText: true,
		autoHeaderHeight: true
	};

	const gridOptions = {
		defaultColDef: defaultColDef,
		pagination: false,
		suppressPaginationPanel: true,
		paginationPageSize: 10,
		suppressHorizontalScroll: true,
		autoHeight: true
	};

	const grid = useRef();

	const reasonForRequest = (props) => {
		const { reauthorizationRequiredIndicator = false, userAgent = '' } = props;
		const onlyConsentChange = activeCR && isEmpty(xor(activeCR.proposedAppDetails.canonicals, activeCR.existingAppDetails.canonicals));
		const newSubscription = userAgent.includes("consent manager:new subscription notification.");
		const requestPaused = userAgent.includes("consent manager:request reconsent:access paused") || (onlyConsentChange && !newSubscription) || (!activeCR && !newSubscription);

		return (<div>
			<div className="text-body-01">
				{reauthorizationRequiredIndicator ? (
					<span title="App requires preauthorization">
						<strong>App requires </strong><div>preauthorization</div>
					</span>
				): requestPaused ? (
					<span title="App requires reauthorization">
						<strong>App requires </strong><div>reauthorization</div>
					</span>
				) : (
					<span title="New subscription requires authorization">
						<strong>New subscription </strong><div>requires authorization</div>
					</span>
				)}
			</div>
		</div>);
	}
	const reauthorizationRequiredIndicator = (props) => {
		const { reauthorizationRequiredIndicator = false, userAgent = '' } = props;
		const newSubscription = userAgent.includes('consent manager:new subscription notification.');
		const onlyConsentChange = activeCR && isEmpty(xor(activeCR.proposedAppDetails.canonicals, activeCR.existingAppDetails.canonicals));
		const requestPaused = userAgent.includes("consent manager:request reconsent:access paused") || (onlyConsentChange && !newSubscription) || (!activeCR && !newSubscription);
		return (<div className="text-body-01">
			{reauthorizationRequiredIndicator ?
				<span>Any practitioner in customer organization</span>
				: requestPaused ? <span>Any practitioner in your organization</span> : <span>Practitioner - original purchaser</span>
			}
		</div>);
	};

	const getOrgName = (props, type) => {
		const { organizationName, ooid } = props;
		return (<div className="text-body-04 leading-xl">
			{organizationName === 'N/A' ? <SdfLink size="sm" onClick={() => { retrieveOrgName(ooid, type); }}>Get Org Name</SdfLink>
				: <div className="text-body-03"> {organizationName} </div>}
			<div className="text-body-01">{ooid}</div>
		</div>);
	};

	const getSubscription = (props) => {
		const { subscriptionStatusCode } = props;
		const status = {
			Active: 'complete',
			Suspended: 'changed',
			Cancelled: 'failed'
		};
		return (
			<SdfBadge status={status[subscriptionStatusCode] || 'default'}>{subscriptionStatusCode}</SdfBadge>
		);
	};

	const getStatus = (props) => {
		const { consentDateTime } = props;
		return (
			<div className="text-body-01 mt-1">{`${consentDateTime ? CONSENT_TYPES[type] + ' ' + getFormattedDate(new Date(consentDateTime)) : 'N/A'}`}</div>
		);
	};

	const getSubscriberInfo = (props) => {
		return (
			<div className="text-body-04 leading-xl">
				<SdfLink href="#" onClick={() => retrieveAssociateInfoAndShow(props)}>Get subscriber info</SdfLink>
			</div>
		);
	};

	const retrieveAssociateInfoAndShow = async (props) => {
		setIsLoading(true);
		setModalOpen(true);
		setSelectedRow(props);
		try {
			const { data } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/project/${projectId}/associate/${props.ooid}/${props.consenterAoid}`);
			const contactInfo = get(data, 'associateContactInfoCollection.associateContactInfo', []);
			const getContactByType = (type, { contMechPurpTypeNm, communicationType }) => {
				return contMechPurpTypeNm === 'BUSINESS' && communicationType === type;
			};
			const workEmail = contactInfo.find(c => getContactByType('EMAIL', c));
			if (workEmail) {
				data.email = workEmail.contactInfo;
			}
			setAssociateDetails(data);
		} catch (e) {
			setShouldDisplayAlertError(true);
		} finally {
			setIsLoading(false);
		}
	};

	const onCloseModal = () => {
		setModalOpen(false);
		setShouldDisplayAlertError(false);
	};

	const columnDefs = [
		{
			field: "organizationName",
			headerName: "Organization name and OOID",
			headerClass: "bg-action-default-100 rounded-tl-lg",
			sortable: true,
			suppressMovable: true,
			suppressColumnsToolPanel: true,
			cellRenderer: ({ data }) => getOrgName(data, type),
			flex: type === 'requestsForAuth' ? 1.25 : 3.25,
			cellStyle: { whiteSpace: 'normal', height: 'auto' },
			autoHeight: true,
			wrapText: true,
			minWidth: 250,
			cellClass: "flex py-2 align-items: center;"
		},
		{
			field: "reasonForRequest",
			headerName: "Reason for request",
			headerClass: "bg-action-default-100",
			sortable: false,
			suppressMovable: true,
			suppressColumnsToolPanel: true,
			cellRenderer: ({ data }) => reasonForRequest(data),
			cellStyle: { whiteSpace: 'normal' },
			autoHeight: true,
			wrapText: true,
			flex: 1,
			hide: type !== 'requestsForAuth',
			cellClass: "flex py-2 align-items: center;"
		},
		{
			field: "consentTypeCode",
			headerName: "Eligible to authorize",
			headerClass: "bg-action-default-100",
			sortable: false,
			suppressMovable: true,
			suppressColumnsToolPanel: true,
			cellRenderer: ({ data }) => reauthorizationRequiredIndicator(data),
			cellStyle: { whiteSpace: 'normal' },
			autoHeight: true,
			wrapText: true,
			flex: 1,
			hide: type !== 'requestsForAuth',
			cellClass: "flex py-2 align-items: center;"
		},
		{
			field: "subscriptionStatusCode",
			headerName: "Subscription",
			headerClass: "bg-action-default-100",
			sortable: true,
			suppressMovable: true,
			cellRenderer: ({ data }) => getSubscription(data),
			cellStyle: { whiteSpace: 'normal' },
			autoHeight: true,
			wrapText: true,
			flex: 0.9,
			cellClass: "flex py-2 align-items: center;"
		},
		{
			field: "consentDateTime",
			headerClass: "bg-action-default-100",
			headerName: "Status",
			sortable: true,
			suppressMovable: true,
			cellRenderer: ({ data }) => getStatus(data),
			cellStyle: { whiteSpace: 'normal' },
			autoHeight: true,
			wrapText: true,
			flex: 1,
			cellClass: "flex py-2 align-items: center;"
		},
		{
			field: "Get subscriber info",
			headerClass: "bg-action-default-100 rounded-tr-lg",
			headerName: "",
			sortable: false,
			suppressMovable: true,
			suppressColumnsToolPanel: true,
			cellStyle: { textAlign: 'center', whiteSpace: 'normal' },
			cellRenderer: ({ data }) => getSubscriberInfo(data),
			autoHeight: true,
			wrapText: true,
			flex: 0.8,
			cellClass: "flex py-2 align-items: center;"
		}
	];

	return (
		<div>
			{isLoadingSubscriptions &&
				<SdfBusyIndicator
					style={{ color: "#1a8099" }}
					size="xl"
					fullScreen={true}
				/>
			}
			<SdfGrid className="ag-theme-alpine">
				<AgGridReact
					ref={grid}
					domLayout='autoHeight'
					gridOptions={gridOptions}
					icons={myIcons}
					rowData={paginatedData}
					rowClass="template-row cursor-pointer"
					overlayNoRowsTemplate="No subscriptions found at this time."
					autoSizeAllColumns={true}
					unSortIcon={true}
					onSortChanged={onSortChanged}
					columnDefs={columnDefs}
				>
					
				</AgGridReact>
			</SdfGrid>
			{subscriptionList.length >= 1 &&
				<div className="flex items-center justify-end pt-4 pb-5">
					<div className="text-sm text-action-default">Rows per page:</div>
					<SdfSelect
						className="w-20 text-sm text-action-default px-4 mb-1 border-r-1 border-t-0 border-l-0 border-b-0 border-dark border-solid"
						items={availablePageSizes}
						onSdfChange={({ detail }) => {
							setPageSize(detail.value);
						}}
						value={{ label: `${pageSize}`, value: pageSize }}
					/>
					<div className="text-sm text-action-default px-4">{`${start} - ${end} items`}</div>
					<SdfxPagination
						currentPage={pageNo}
						totalPages={totalPages}
						onSdfxPageChange={({ detail }) => {
							setPageNo(detail);
						}}
					/>
				</div>
			}
			{modalOpen && <SubscriberDetails
				associateDetails={associateDetails}
				onClose={onCloseModal}
				loading={isLoading}
				shouldDisplayAlertError={shouldDisplayAlertError}
				consenterAoid={selectedRow.consenterAoid}
			/>}
		</div>
	);
}
